import TagSelector from '@/components/TagSelector';
import { useEffect, useState } from 'react';

/**
 * Contacts Filter Modal Component
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Close modal handler
 * @param {Object} props.filters - Current filter values
 * @param {Function} props.onApplyFilters - Apply filters handler
 * @returns {JSX.Element} ContactsFilterModal component
 */
const ContactsFilterModal = ({ isOpen, onClose, filters, onApplyFilters }) => {
  const [localFilters, setLocalFilters] = useState({
    tags: filters.tags || [],
    from: filters.from || '',
    to: filters.to || '',
  });

  // Update local state when props change
  useEffect(() => {
    setLocalFilters({
      tags: filters.tags || [],
      from: filters.from || '',
      to: filters.to || '',
    });
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setLocalFilters({
      tags: [],
      from: '',
      to: '',
    });
  };

  // Apply all changes
  const handleApply = () => {
    onApplyFilters(localFilters);
    onClose();
  };

  // Reset to defaults
  const _handleReset = () => {
    setLocalFilters({
      tags: [],
      from: '',
      to: '',
    });
  };

  // Check if there are any changes
  const hasChanges = () => {
    return JSON.stringify(localFilters) !== JSON.stringify(filters);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4 text-center">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="relative inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-2xl">
          {/* Header */}
          <div className="bg-white px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Filter Contacts
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Apply filters to refine your contact list
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 transition-colors"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white px-6 py-6 max-h-[70vh] overflow-y-auto">
            {/* Filters Section */}
            <div className="space-y-5">
              {/* Tags Filter */}
              <div>
                <TagSelector
                  selectedTags={localFilters.tags}
                  onChange={value => handleFilterChange('tags', value)}
                  allowCreation={false}
                />
              </div>

              {/* Created At Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Created At Range
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="filter-from"
                      className="block text-xs text-gray-500 mb-1"
                    >
                      From
                    </label>
                    <input
                      type="date"
                      id="filter-from"
                      value={localFilters.from}
                      onChange={e => handleFilterChange('from', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="filter-to"
                      className="block text-xs text-gray-500 mb-1"
                    >
                      To
                    </label>
                    <input
                      type="date"
                      id="filter-to"
                      value={localFilters.to}
                      onChange={e => handleFilterChange('to', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Clear Filters Button */}
              <div className="pt-2 border-t border-gray-200">
                <button
                  onClick={clearFilters}
                  className="text-sm text-primary-600 hover:text-primary-800 font-medium transition-colors"
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={!hasChanges()}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactsFilterModal;
